import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { vault, vaultEffects, vaultLayout, vaultMedia, vaultRadii } from './design-tokens';

const focusRing = {
  '&:focus-visible': { outline: `2px solid ${vault.accent}`, outlineOffset: 2 },
};

export const followingStyles: Record<string, SystemStyleObject<Theme>> = {
  page: {
    minHeight: '100vh',
    color: vault.fg,
    background: `${vaultEffects.heroGlowLeft}, ${vault.bg}`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100% 420px, auto',
  },
  inner: {
    maxWidth: 900,
    mx: 'auto',
    padding: `64px ${vaultLayout.gutter} 72px`,
    [vaultMedia.mobile]: { padding: `40px ${vaultLayout.gutterMobile} 48px` },
  },
  statusPanel: {
    padding: '28px',
    borderRadius: vaultRadii.panel,
    backgroundColor: vault.surface,
    border: `1px solid ${vault.line}`,
    textAlign: 'center',
    fontSize: 15,
    color: vault.muted,
  },

  // ─── Header ────────────────────────────────────────────────────────────────
  hero: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: '24px 32px',
    marginBottom: '40px',
    [vaultMedia.mobile]: { flexDirection: 'column', alignItems: 'stretch', gap: '24px', marginBottom: '32px' },
  },
  eyebrow: {
    marginBottom: '16px',
    [vaultMedia.mobile]: { marginBottom: '12px' },
  },
  title: {
    margin: 0,
    fontSize: 56,
    lineHeight: 1,
    fontWeight: 600,
    letterSpacing: '-0.035em',
    color: vault.fg,
    [vaultMedia.mobile]: { fontSize: 40 },
  },

  // ─── Section ───────────────────────────────────────────────────────────────
  sectionHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: '12px',
    paddingBottom: '12px',
    marginBottom: '20px',
    borderBottom: `1px solid ${vault.lineStrong}`,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: 600,
    letterSpacing: '-0.02em',
    color: vault.fg,
  },
  intro: {
    margin: '0 0 24px',
    maxWidth: 640,
    fontSize: 15,
    lineHeight: 1.6,
    color: vault.muted,
  },

  // ─── Add row (autocomplete + button) ───────────────────────────────────────
  addRow: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
  },
  field: {
    flex: 1,
    minWidth: 0,
    '& .MuiOutlinedInput-root': {
      minHeight: 44,
      borderRadius: '9px',
      backgroundColor: vault.surface,
      color: vault.fg,
      fontSize: 14,
      '& fieldset': { borderColor: vault.lineStrong },
      '&:hover fieldset': { borderColor: vault.faint },
      '&.Mui-focused fieldset': { borderColor: vault.accent, borderWidth: 1 },
    },
    '& .MuiInputBase-input::placeholder': { color: vault.faint, opacity: 1 },
    '& .MuiSvgIcon-root': { color: vault.faint },
  },
  addButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    flex: 'none',
    height: 44,
    padding: '0 18px',
    border: 'none',
    borderRadius: '9px',
    backgroundColor: vault.accent,
    color: vault.onAccent,
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: `opacity ${vaultEffects.transition}, box-shadow ${vaultEffects.transition}`,
    '&:hover:not(:disabled)': { boxShadow: `0 0 0 4px ${vault.glow}` },
    '&:disabled': {
      cursor: 'default',
      backgroundColor: vault.chipStrong,
      color: vault.faint,
    },
    ...focusRing,
    [vaultMedia.mobile]: { padding: '0 14px' },
  },

  // ─── Feedback ──────────────────────────────────────────────────────────────
  alert: {
    marginBottom: '16px',
    borderRadius: vaultRadii.input,
    fontSize: 14,
  },
  alertSuccess: {
    backgroundColor: vault.okBg,
    color: vault.ok,
    border: `1px solid ${vault.ok}`,
    '& .MuiAlert-icon': { color: vault.ok },
  },

  // ─── Lists ─────────────────────────────────────────────────────────────────
  list: {
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '10px 12px',
    margin: '0 -12px',
    borderRadius: vaultRadii.card,
    borderBottom: `1px solid ${vault.line}`,
    transition: `background-color ${vaultEffects.transition}`,
    '&:hover': { backgroundColor: vault.chip },
    [vaultMedia.mobile]: { gap: '10px' },
  },
  rowLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    flex: 1,
    minWidth: 0,
    color: vault.fg,
    textDecoration: 'none',
    borderRadius: vaultRadii.input,
    '&:hover .following-name': { color: vault.accentText },
    ...focusRing,
    [vaultMedia.mobile]: { gap: '10px' },
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: vault.chipStrong,
    border: `1px solid ${vault.line}`,
    display: 'grid',
    placeItems: 'center',
    flex: 'none',
    fontSize: 12,
    fontWeight: 600,
    color: vault.muted,
    textTransform: 'uppercase',
    [vaultMedia.mobile]: { width: 30, height: 30, fontSize: 11 },
  },
  name: {
    flex: 1,
    minWidth: 0,
    fontSize: 16,
    fontWeight: 600,
    letterSpacing: '-0.01em',
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    transition: `color ${vaultEffects.transition}`,
    [vaultMedia.mobile]: { fontSize: 15 },
  },
  removeButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    flex: 'none',
    height: 32,
    padding: '0 12px',
    borderRadius: vaultRadii.pill,
    border: `1px solid ${vault.lineStrong}`,
    backgroundColor: 'transparent',
    color: vault.muted,
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    transition: `color ${vaultEffects.transition}, border-color ${vaultEffects.transition}`,
    '&:hover': { color: vault.fg, borderColor: vault.faint },
    ...focusRing,
    [vaultMedia.mobile]: {
      width: 40,
      height: 40,
      padding: 0,
      justifyContent: 'center',
      '& .following-remove-label': { display: 'none' },
    },
  },
  empty: {
    padding: '20px 0',
    fontSize: 15,
    color: vault.muted,
  },

  // ─── Pagination ────────────────────────────────────────────────────────────
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '24px',
    '& .MuiPaginationItem-root': {
      color: vault.muted,
      borderRadius: vaultRadii.input,
      fontSize: 13,
      '&:hover': { backgroundColor: vault.chip },
      '&.Mui-selected': {
        backgroundColor: vault.chipStrong,
        color: vault.fg,
        fontWeight: 600,
        '&:hover': { backgroundColor: vault.chipStrong },
      },
    },
  },
};
